import { useEffect, useState } from "react";
import api, { API, withCompany } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Users, FileDown } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FirmDetailSheet from "@/components/FirmDetailSheet";
import MergeDialog from "@/components/MergeDialog";
import ExcelButton from "@/components/ExcelButton";
import { DiffBadge } from "@/components/FirmScorecard";
import { fmtTL, fmtDate } from "@/lib/utils";

export default function Firms() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const [all, setAll] = useState([]);
  const load = () => api.get("/firms", { params: q ? { q } : {} }).then(r => setRows(r.data));
  const loadAll = () => api.get("/firms").then(r => setAll(r.data));
  const [scores, setScores] = useState({});
  const loadScores = () => api.get("/scorecards").then(r => setScores(Object.fromEntries(r.data.map(x => [x.firm_id, x]))));
  useEffect(() => { loadAll(); loadScores(); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);

  const max = Math.max(1, ...rows.map(r => r.total_amount));

  return (
    <div data-testid="firms-page">
      <PageHeader title="Firmalar (Taşeronlar)" subtitle="Çalışılan firmalar, projeleri ve hakediş toplamları.">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9 w-72" placeholder="Firma ara..." value={q} onChange={e => setQ(e.target.value)} data-testid="firm-search" />
        </div>
        <MergeDialog type="firm" entities={all} onMerged={() => { load(); loadAll(); loadScores(); }} />
        <ExcelButton testid="firms-excel-button" />
        <Button variant="outline" onClick={() => { window.location.href = withCompany(`${API}/reports/scorecards.xlsx`); }} data-testid="scorecard-excel-button"
          className="border-rose-500/40 text-rose-700 dark:text-rose-400 hover:bg-rose-500/10"><FileDown className="w-4 h-4 mr-1" /> Karne Excel</Button>
      </PageHeader>
      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <Users className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Firma bulunamadı</div>
          <p className="text-sm text-muted-foreground">Veri Aktarımı sayfasından hakediş Excel dosyası yükleyin.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Firma</TableHead><TableHead className="text-right">Proje</TableHead><TableHead className="text-right">Hakediş</TableHead>
                <TableHead>Çalışma Dönemi</TableHead><TableHead title="Aynı yıl, aynı kalemde diğer firmalara göre ağırlıklı fiyat farkı">Fiyat Karnesi</TableHead><TableHead className="w-[28%]">Toplam Hakediş</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-amber-500/5" onClick={() => setSelected(r.id)} data-testid={`firm-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}
                    {r.merged_names?.length > 1 && <div className="text-[11px] text-muted-foreground font-normal">{r.merged_names.length} kayıt birleştirildi</div>}
                  </TableCell>
                  <TableCell className="text-right font-mono-nums">{r.project_count}</TableCell>
                  <TableCell className="text-right font-mono-nums">{r.payment_count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(r.first_date)} — {fmtDate(r.last_date)}</TableCell>
                  <TableCell>{scores[r.id]?.compared_items ? <DiffBadge pct={scores[r.id].weighted_diff_pct} testid={`firm-score-${r.id}`} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(r.total_amount / max) * 100}%` }} />
                      </div>
                      <span className="font-mono-nums text-sm font-semibold w-36 text-right">{fmtTL(r.total_amount)}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <FirmDetailSheet firmId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
